import type { BannerProps } from '@neo4j-ndl/react';
import { Banner, LoadingSpinner, usePrevious } from '@neo4j-ndl/react';
import { getFlag } from '@nx/launch-darkly-service';
import type { ApiError } from '@nx/state';
import { consoleApi, getApiError, useActiveOrgQuery, useActiveProjectQuery, useUnsafeAppContext } from '@nx/state';
import { isNonEmptyString, isNotNullish, isNullish } from '@nx/stdlib';
import { Center } from '@nx/ui';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router';

import { ApiErrorBanner } from '../components/api-error';
import { MfaEnabledError } from '../organization/mfa';
import { useIsOrgContext } from './hooks';
import { maybeAuthenticateWithSsoOrg, useAppContextInit } from './use-app-context-init';

const REFRESH_AND_SUPPORT_MESSAGE = 'Refresh the page and if the problem persists contact customer support.';

const CenteredLoadingSpinner = () => (
  <Center>
    <LoadingSpinner size="large" />
  </Center>
);

const ErrorBanner = ({ description, title = 'Error' }: Pick<BannerProps, 'description' | 'title'>) => (
  <Banner className="mx-auto my-8 h-fit w-fit" type="danger" title={title} description={description} usage="inline" />
);

const useIsFetchingAfterValueChange = (isFetching: boolean, value: string | null) => {
  const [isFetchingAfterChange, setIsFetchingAfterChange] = useState(false);
  const prevValue = usePrevious(value);
  const justChanged = isNonEmptyString(prevValue) && value !== prevValue;
  useEffect(() => {
    if (justChanged) {
      setIsFetchingAfterChange(true);
    }
    if (!isFetching && isFetchingAfterChange) {
      setIsFetchingAfterChange(false);
    }
  }, [isFetching, isFetchingAfterChange, justChanged]);

  return isFetching && (justChanged || isFetchingAfterChange);
};

type AppContextLoaderProps = {
  /**
   * If `true` app context is initialized without showing loading and error elements.
   * Instead, the Outlet component is rendered immediately.
   */
  loadInBackground?: boolean;
};

const getApiErrorBanner = ({ error }: { error: ApiError | null }) => {
  if (isNotNullish(error) && isNotNullish(error.message)) {
    return <ErrorBanner description={error.message} />;
  }

  return undefined;
};

const OrgProjectContextLoader = ({ loadInBackground }: { loadInBackground: boolean }) => {
  const { activeOrgId, activeProjectId } = useUnsafeAppContext();
  const isOrgContext = useIsOrgContext();
  const isUpxConsistencyEnabled = getFlag('upxConsistency', false);

  const getOrgRes = useActiveOrgQuery({
    refetchOnMountOrArgChange: true,
  });
  const getProjectRes = useActiveProjectQuery({
    refetchOnMountOrArgChange: true,
  });

  const isFetchingAfterOrgChange = useIsFetchingAfterValueChange(getOrgRes.isFetching, activeOrgId);
  const isFetchingAfterProjectChange = useIsFetchingAfterValueChange(getProjectRes.isFetching, activeProjectId);

  if (loadInBackground) {
    return <Outlet />;
  }

  if (isNullish(activeOrgId)) {
    return (
      <ErrorBanner
        description={`Active organization cannot be found. This indicates an internal error. ${REFRESH_AND_SUPPORT_MESSAGE}`}
      />
    );
  }

  if (isNotNullish(getOrgRes.error)) {
    if (getApiError(getOrgRes.error).reason === 'org_requires_mfa' && isUpxConsistencyEnabled) {
      return <MfaEnabledError />;
    }

    return <ApiErrorBanner error={getOrgRes.error} />;
  }

  if (getOrgRes.isLoading || isFetchingAfterOrgChange || getProjectRes.isLoading || isFetchingAfterProjectChange) {
    return <CenteredLoadingSpinner />;
  }

  if (isOrgContext && !getOrgRes.data) {
    return null;
  }

  if (!isOrgContext && (!getOrgRes.data || !getProjectRes.data)) {
    return null;
  }

  return <Outlet />;
};

export const SsoContextLoader = ({ loadInBackground }: { loadInBackground: boolean }) => {
  const [ssoAuthenticateLoading, setSsoAuthenticateLoading] = useState(true);
  const { activeOrgId, userId } = useUnsafeAppContext();
  const prevActiveOrgId = usePrevious(activeOrgId);

  // This list call is already made in the useappContextInit hook, so this should
  // just return cached data on initial render
  const { data: orgs, isFetching, refetch } = consoleApi.useListOrganizationsByUserQuery(userId ?? skipToken);

  const isFetchingAfterOrgChange = useIsFetchingAfterValueChange(isFetching, activeOrgId);

  const activeOrg = orgs?.find((o) => o.id === activeOrgId);

  useEffect(() => {
    if (!activeOrg) {
      return;
    }

    // We are loading orgs, dont try to authenticate yet
    if (isFetchingAfterOrgChange) {
      setSsoAuthenticateLoading(true);
      return;
    }

    // No sso org id, no need to authenticate
    if (isNullish(activeOrg.ssoOrgId)) {
      setSsoAuthenticateLoading(false);
      return;
    }

    const handler = async () => {
      setSsoAuthenticateLoading(true);
      await maybeAuthenticateWithSsoOrg(activeOrg.ssoOrgId!);
      setSsoAuthenticateLoading(false);
    };

    void handler();
  }, [activeOrg, isFetchingAfterOrgChange]);

  useEffect(() => {
    // When selected org changes, we want to relist the users orgs
    // in case sso has been configured since they last changed orgs
    if (isNotNullish(activeOrgId) && isNotNullish(prevActiveOrgId) && activeOrgId !== prevActiveOrgId) {
      void refetch();
    }
  }, [activeOrgId, prevActiveOrgId, refetch]);

  if (loadInBackground) {
    return <OrgProjectContextLoader loadInBackground={loadInBackground} />;
  }

  if (isNullish(activeOrgId)) {
    return (
      <ErrorBanner
        description={`Active organization cannot be found. This indicates an internal error. ${REFRESH_AND_SUPPORT_MESSAGE}`}
      />
    );
  }

  if (isFetchingAfterOrgChange || ssoAuthenticateLoading) {
    return <CenteredLoadingSpinner />;
  }

  if (!activeOrg) {
    return (
      <ErrorBanner
        description={`Failed to find active organization in the users organizations. ${REFRESH_AND_SUPPORT_MESSAGE}`}
      />
    );
  }

  return <OrgProjectContextLoader loadInBackground={loadInBackground} />;
};

export const AppContextLoader = ({ loadInBackground = false }: AppContextLoaderProps) => {
  const init = useAppContextInit();

  if (loadInBackground) {
    return <SsoContextLoader loadInBackground={loadInBackground} />;
  }

  const errorBanner = getApiErrorBanner({ error: init.error });
  if (isNotNullish(errorBanner)) {
    return errorBanner;
  }

  if (init.isLoading) {
    return <CenteredLoadingSpinner />;
  }

  return <SsoContextLoader loadInBackground={loadInBackground} />;
};
